import { defineStore } from 'pinia';
import { 
  UserType, 
  QueueStage, 
  GenerationStatus,
  type QueueTask, 
  type CreateQueueTaskParams, 
  type QueueCallbacks 
} from '@/types/taskQueue';
import { DEFAULT_QUEUE_CONFIG } from '@/hooks/usetaskQueue2/constants';
import { UserStrategyFactory, type BaseUserStrategy } from '@/hooks/usetaskQueue2/strategies/userTypeStrategy';
import { EventTrackerFactory, type IEventTracker } from '@/hooks/usetaskQueue2/tracking/eventTracker';
import { ModuleEnum } from "@/assets/artwork/moduleEnum";

/** 队列管理器状态 */
interface TaskQueueState {
  /** 当前活跃的任务 */
  activeTasks: Map<string, QueueTask>;
  /** 用户策略实例 */
  userStrategies: Map<UserType, BaseUserStrategy>;
  /** 事件追踪器 */
  eventTracker: IEventTracker;
  /** 定时器集合 */
  timers: Map<string, NodeJS.Timeout>;
}

export const useTaskQueueStore = defineStore('taskQueue', {
  state: (): TaskQueueState => ({
    activeTasks: new Map(),
    userStrategies: new Map(),
    eventTracker: EventTrackerFactory.createTracker(DEFAULT_QUEUE_CONFIG.tracking.enabled),
    timers: new Map()
  }),

  getters: {
    /** 获取活跃任务列表 */
    activeTaskList: (state) => Array.from(state.activeTasks.values()),
    
    /** 获取活跃任务数量 */
    activeTaskCount: (state) => state.activeTasks.size,
    
    /** 获取所有活跃任务的 clientId */
    activeClientIds: (state) => Array.from(state.activeTasks.keys()),
  },

  actions: {
    /** 初始化用户策略 */
    initializeUserStrategies() {
      Object.entries(DEFAULT_QUEUE_CONFIG.userStrategies).forEach(([userType, strategyConfig]) => {
        const strategyInstance = UserStrategyFactory.createStrategy(userType as UserType, strategyConfig);
        this.userStrategies.set(userType as UserType, strategyInstance);
      });
    },

    /** 创建队列任务 */
    createQueueTask(params: CreateQueueTaskParams): QueueTask {
      const now = Date.now();
      
      const task: QueueTask = {
        clientId: params.clientId,
        module: params.module,
        stage: QueueStage.INITIALIZING,
        status: GenerationStatus.QUEUING,
        userType: params.userType,
        queueNum: 0,
        queueTotalNum: 0,
        progress: 0,
        message: '正在初始化...',
        estimatedWaitTime: 0,
        totalWaitTime: 0,
        createdAt: now,
        stageStartTime: now,
        useNewQueue: params.useNewQueue,
        aspectRatios: params.aspectRatios
      };

      return task;
    },

    /** 启动队列任务 */
    startQueueTask(params: CreateQueueTaskParams): QueueTask {
      // 确保策略已初始化
      if (this.userStrategies.size === 0) {
        this.initializeUserStrategies();
      }
      
      const task = this.createQueueTask(params);
      
      // 存储任务
      this.activeTasks.set(params.clientId, task);
      
      // 获取用户策略
      const strategy = this.userStrategies.get(params.userType);
      if (!strategy) {
        throw new Error(`No strategy found for user type: ${params.userType}`);
      }
      
      // 追踪队列进入事件
      this.eventTracker.trackQueueEnter(task);
      
      // 开始处理任务  
      // 使用类型断言解决 TypeScript 类型推断问题
      this.processTask(task, strategy as any, params.callbacks);
      
      return task;
    },

    /** 获取任务状态 */
    getTaskStatus(clientId: string): QueueTask | undefined {
      const task = this.activeTasks.get(clientId);
   
      return task;
    },

    /** 更新任务状态 */
    updateTask(clientId: string, updates: Partial<QueueTask>) {
      const task = this.activeTasks.get(clientId);
      if (task) {
        // 创建新的任务对象以确保响应式更新
        const updatedTask = { ...task, ...updates };
        this.activeTasks.set(clientId, updatedTask);
      }
    },

    /** 取消任务 */
    cancelTask(clientId: string, reason = 'user_cancelled'): void {
      const task = this.activeTasks.get(clientId);
      if (task) {
        // 追踪用户退出事件
        this.eventTracker.trackUserExit(task, reason);
        
        // 使用响应式更新
        this.updateTask(clientId, {
          stage: QueueStage.ERROR,
          status: GenerationStatus.ERROR_OR_CANCELLED,
          message: '已取消'
        });
        
        this.cleanupTask(clientId);
      }
    },

    /** 外部生图开始 - 跳转到生成阶段 */
    handleExternalGenerationStart(clientId: string): boolean {
      const task = this.activeTasks.get(clientId);
      if (!task) {
        return false;
      }

      // 如果已经在生成阶段，无需处理
      if (task.stage === QueueStage.GENERATING) {
        return true;
      }

      // 清理所有相关定时器
      for (const [key, timer] of this.timers.entries()) {
        if (key.startsWith(clientId)) {
          clearTimeout(timer);
          this.timers.delete(key);
        }
      }

      // 计入当前阶段的等待时间
      const stageTime = (Date.now() - task.stageStartTime) / 1000;
      const newTotalWaitTime = task.totalWaitTime + stageTime;

      // 为生成阶段设置预计等待时间
      const estimatedGenerationTime = this.calculateGenerationEstimatedTime(task);

      // 更新任务状态到生成阶段
      this.updateTask(clientId, {
        stage: QueueStage.GENERATING,
        status: GenerationStatus.GENERATING,
        stageStartTime: Date.now(),
        totalWaitTime: newTotalWaitTime,
        estimatedWaitTime: estimatedGenerationTime,
        message: '正在生成中...'
      });

      // 启动生成时间倒计时
      this.startEstimatedTimeCountdown(clientId, estimatedGenerationTime);

      // 获取用户策略，启动生成阶段的处理逻辑
      const strategy = this.userStrategies.get(task.userType);
      if (strategy) {
        // 开始进度更新
        this.startProgressUpdates(task, strategy as any, undefined);
      }

      return true;
    },

    /** 外部生图完成 */
    handleExternalGenerationComplete(clientId: string): boolean {
      const task = this.activeTasks.get(clientId);
      if (!task) {
        return false;
      }
      
      // 更新到完成状态
      this.updateTask(clientId, {
        stage: QueueStage.COMPLETED,
        status: GenerationStatus.COMPLETED,
        progress: 100,
        message: '生成完成'
      });

      // 获取更新后的任务
      const updatedTask = this.activeTasks.get(clientId);
      if (updatedTask) {
        // 追踪生成完成事件
        this.eventTracker.trackGenerationComplete(updatedTask, {
          success: true,
          totalWaitTime: updatedTask.totalWaitTime,
          actualGenerationTime: (Date.now() - updatedTask.createdAt) / 1000
        });
      }

      this.cleanupTask(clientId);
      return true;
    },

    /** 外部生图失败 */
    handleExternalGenerationError(clientId: string, errorMessage?: string): boolean {
      const task = this.activeTasks.get(clientId);
      if (!task) {
        return false;
      }
      
      // 更新到错误状态
      this.updateTask(clientId, {
        stage: QueueStage.ERROR,
        status: GenerationStatus.ERROR_OR_CANCELLED,
        message: errorMessage || '生成失败'
      });

      // 获取更新后的任务
      const updatedTask = this.activeTasks.get(clientId);
      if (updatedTask) {
        // 追踪生成完成事件
        this.eventTracker.trackGenerationComplete(updatedTask, {
          success: false,
          totalWaitTime: updatedTask.totalWaitTime,
          actualGenerationTime: (Date.now() - updatedTask.createdAt) / 1000,
          errorMessage: updatedTask.message
        });
      }

      this.cleanupTask(clientId);
      return true;
    },

    /** 计算生成阶段的预计等待时间 */
    calculateGenerationEstimatedTime(task: QueueTask): number {
      // 特殊模块处理
      if (task.module === ModuleEnum.render) {
        if (task.quality === '1K') {
          return 5;
        }
        return 10; // 万能渲染默认10秒
      }
      if (task.module === ModuleEnum.matting) {
        return 2; // 一键抠图默认2秒
      }
      if (task.module === ModuleEnum.aivideo) {
        return 45; // 视频默认45秒
      }

      // 根据用户类型和模块返回不同的预计生成时间
      const baseTime = 120; // 基础生成时间 2分钟
      
      // 根据用户类型调整时间
      const userTypeMultiplier = {
        [UserType.FREE]: 1.5,    // 免费用户稍慢
        [UserType.VIP]: 1.0,     // 标准版正常
        [UserType.SVIP]: 0.8     // 专业版更快
      };
      
      const multiplier = userTypeMultiplier[task.userType] || 1.0;
      return Math.floor(baseTime * multiplier);
    },

    /** 启动预计等待时间倒计时 */
    startEstimatedTimeCountdown(clientId: string, initialTime: number): void {
      const intervalMs = 1000; // 每秒更新一次
      let remainingTime = initialTime;

      const updateCountdown = () => {
        const currentTask = this.activeTasks.get(clientId);
        if (!currentTask) {
          return;
        }

        remainingTime = Math.max(0, remainingTime - 1);
        
        this.updateTask(clientId, {
          estimatedWaitTime: remainingTime
        });

        if (remainingTime > 0) {
          const timer = setTimeout(updateCountdown, intervalMs);
          this.timers.set(`${clientId}_countdown`, timer);
        }
      };

      // 首次 1 秒后再减 1，避免进入生成阶段时 initialGeneratingTime 被错误记录为 (初始值-1)
      const timer = setTimeout(updateCountdown, intervalMs);
      this.timers.set(`${clientId}_countdown`, timer);
    },

    /** 清理任务 */
    cleanupTask(clientId: string): void {
      // 清理定时器
      for (const [key, timer] of this.timers.entries()) {
        if (key.startsWith(clientId)) {
          clearTimeout(timer);
          this.timers.delete(key);
        }
      }

      // 移除任务
      this.activeTasks.delete(clientId);
    },

    /** 清理所有资源 */
    cleanup(): void {
      // 清理所有定时器
      this.timers.forEach(timer => clearTimeout(timer));
      this.timers.clear();

      // 清理用户策略
      this.userStrategies.forEach(strategy => {
        if ('destroy' in strategy && typeof strategy.destroy === 'function') {
          strategy.destroy();
        }
      });
      this.userStrategies.clear();

      // 清理事件追踪器
      if ('destroy' in this.eventTracker && typeof this.eventTracker.destroy === 'function') {
        this.eventTracker.destroy();
      }

      // 清理活跃任务
      this.activeTasks.clear();
    },

    /** 处理任务流程 */
    processTask(task: QueueTask, strategy: BaseUserStrategy, callbacks?: QueueCallbacks): void {
      const processStage = (stage: QueueStage) => {
        const previousStage = task.stage;
        
        // 更新阶段 - 使用响应式更新
        this.updateTask(task.clientId, {
          stage: stage,
          stageStartTime: Date.now(),
          message: strategy.getStageConfig(stage).config?.message || strategy.getStageConfig(stage).name
        });
        
        // 获取更新后的任务对象
        const updatedTask = this.activeTasks.get(task.clientId);
        if (!updatedTask) return;
        
        // 追踪阶段变更
        if (previousStage !== stage) {
          this.eventTracker.trackStageChange(updatedTask, previousStage);
          callbacks?.onStageChange?.(updatedTask, previousStage);
        }
        
        // 触发状态更新回调
        callbacks?.onUpdate?.(updatedTask);

        // 处理不同阶段的逻辑
        switch (stage) {
          case QueueStage.INITIALIZING:
            this.handleInitializingStage(updatedTask, strategy, processStage);
            break;
          case QueueStage.QUEUING:
            this.handleQueuingStage(updatedTask, strategy, processStage);
            break;
          case QueueStage.MODEL_LOADING:
            this.handleModelLoadingStage(updatedTask, strategy, processStage);
            break;
          case QueueStage.GENERATING:
            this.handleGeneratingStage(updatedTask, strategy, callbacks);
            break;
          case QueueStage.COMPLETED:
            this.handleCompletedStage(updatedTask, callbacks);
            break;
          case QueueStage.ERROR:
            this.handleErrorStage(updatedTask, callbacks);
            break;
        }
      };

      // 开始处理初始化阶段
      processStage(QueueStage.INITIALIZING);
    },

    /** 处理初始化阶段 */
    handleInitializingStage(task: QueueTask, strategy: BaseUserStrategy, processStage: (stage: QueueStage) => void): void {
      const stageConfig = strategy.getStageConfig(QueueStage.INITIALIZING);
      const maxWaitTime = stageConfig.maxWaitTime * 1000; // 转换为毫秒
      
      const timer = setTimeout(() => {
        // 获取最新的任务状态
        const currentTask = this.activeTasks.get(task.clientId);
        if (!currentTask) {
          return;
        }
        
        // 检查任务是否仍在初始化阶段
        if (currentTask.stage !== QueueStage.INITIALIZING) {
          return;
        }
        
        // 计入总等待时间
        if (stageConfig.countToTotalTime) {
          this.updateTask(task.clientId, {
            totalWaitTime: currentTask.totalWaitTime + stageConfig.maxWaitTime
          });
        }
        
        const nextStage = strategy.getNextStage(QueueStage.INITIALIZING);
        if (nextStage) {
          processStage(nextStage);
        }
      }, maxWaitTime);

      this.timers.set(`${task.clientId}_initializing`, timer);
    },

    /** 处理排队阶段 */
    handleQueuingStage(task: QueueTask, strategy: BaseUserStrategy, processStage: (stage: QueueStage) => void): void {
      // 初始化排队数据
      const queueData = strategy.initializeQueueData(task);

      this.updateTask(task.clientId, queueData);

      // 获取更新后的任务对象，确保传递最新状态
      const updatedTask = this.activeTasks.get(task.clientId);
      if (!updatedTask) {
        return;
      }

      // 开始排队处理，传入更新回调确保响应式更新
      strategy.processQueueing(updatedTask, (updates) => {
        this.updateTask(task.clientId, updates);
      });

      // 监听排队完成
      const checkQueueCompletion = () => {
        // 获取最新的任务状态
        const currentTask = this.activeTasks.get(task.clientId);
        if (!currentTask) {
          return;
        }
        
        // 检查是否被外部回调(如onGenerating)直接修改了阶段
        if (currentTask.stage !== QueueStage.QUEUING) {
          return;
        }
        
        if (currentTask.queueNum <= 0) {
          const stageConfig = strategy.getStageConfig(QueueStage.QUEUING);
          
          // 计入总等待时间
          if (stageConfig.countToTotalTime) {
            const stageTime = (Date.now() - currentTask.stageStartTime) / 1000;
            this.updateTask(currentTask.clientId, {
              totalWaitTime: currentTask.totalWaitTime + stageTime
            });
          }

          const nextStage = strategy.getNextStage(QueueStage.QUEUING);
          if (nextStage) {
            processStage(nextStage);
          }
          return;
        }

        // 检查总等待时间是否超过限制
        if (strategy.isExceedMaxTotalWaitTime(currentTask.totalWaitTime)) {
          // task.stage = QueueStage.ERROR;
          // task.message = '等待时间超过限制';
          // processStage(QueueStage.ERROR);
          // return;
        }

        // 继续检查
        setTimeout(checkQueueCompletion, 500);
      };

      checkQueueCompletion();
    },

    /** 处理模型加载阶段 */
    handleModelLoadingStage(task: QueueTask, strategy: BaseUserStrategy, processStage: (stage: QueueStage) => void): void {
      const stageConfig = strategy.getStageConfig(QueueStage.MODEL_LOADING);
      
      if (!stageConfig.config?.enabled) {
        // 如果不启用此阶段，直接跳转到下一阶段
        const nextStage = strategy.getNextStage(QueueStage.MODEL_LOADING);
        if (nextStage) {
          processStage(nextStage);
        }
        return;
      }

      // 设置预计等待时间为阶段的最大等待时间
      this.updateTask(task.clientId, {
        estimatedWaitTime: stageConfig.maxWaitTime
      });

      const maxWaitTime = stageConfig.maxWaitTime * 1000; // 转换为毫秒

      // 启动倒计时更新预计等待时间
      this.startEstimatedTimeCountdown(task.clientId, stageConfig.maxWaitTime);

      const timer = setTimeout(() => {
        // 获取最新的任务状态
        const currentTask = this.activeTasks.get(task.clientId);
        if (!currentTask) {
          return;
        }
        
        // 检查任务是否仍在模型加载阶段
        if (currentTask.stage !== QueueStage.MODEL_LOADING) {
          return;
        }
        
        // 计入总等待时间
        if (stageConfig.countToTotalTime) {
          this.updateTask(task.clientId, {
            totalWaitTime: currentTask.totalWaitTime + stageConfig.maxWaitTime
          });
        }

        // const nextStage = strategy.getNextStage(QueueStage.MODEL_LOADING);
        // if (nextStage) {
        //   processStage(nextStage);
        // }
      }, maxWaitTime);

      this.timers.set(`${task.clientId}_model_loading`, timer);
    },

    /** 处理生图阶段 */
    handleGeneratingStage(task: QueueTask, strategy: BaseUserStrategy, callbacks?: QueueCallbacks): void {
      // 为生成阶段设置预计等待时间（基于平均生成时间）
      const estimatedGenerationTime = this.calculateGenerationEstimatedTime(task);
      
      this.updateTask(task.clientId, {
        status: GenerationStatus.GENERATING,
        estimatedWaitTime: estimatedGenerationTime
      });
      
      // 启动生成时间倒计时
      this.startEstimatedTimeCountdown(task.clientId, estimatedGenerationTime);
      
      // 开始进度更新
      this.startProgressUpdates(task, strategy, callbacks);
    },

    /** 开始进度更新 */
    startProgressUpdates(task: QueueTask, strategy: BaseUserStrategy, callbacks?: QueueCallbacks): void {
      const stageConfig = strategy.getStageConfig(QueueStage.GENERATING);
      const maxProgress = stageConfig.config?.maxProgressPercentage || 90;

      let currentProgress = 0;
      const progressIncrement = 1; // 每次增加1%
      const updateInterval = 500; // 500ms更新一次

      const updateProgress = () => {
        // 获取最新的任务状态
        const currentTask = this.activeTasks.get(task.clientId);
        if (!currentTask) {
          return;
        }
        
        if (currentTask.stage !== QueueStage.GENERATING) {
          return;
        }

        currentProgress = Math.min(currentProgress + progressIncrement, maxProgress);
        this.updateTask(task.clientId, {
          progress: currentProgress
        });

        // 获取更新后的任务
        const updatedTask = this.activeTasks.get(task.clientId);
        if (updatedTask) {
          callbacks?.onProgressUpdate?.(updatedTask, currentProgress);
          callbacks?.onUpdate?.(updatedTask);
        }

        if (currentProgress < maxProgress) {
          const timer = setTimeout(updateProgress, updateInterval);
          this.timers.set(`${task.clientId}_progress`, timer);
        }
      };

      updateProgress();
    },

    /** 处理完成阶段 */
    handleCompletedStage(task: QueueTask, callbacks?: QueueCallbacks): void {
      this.updateTask(task.clientId, {
        status: GenerationStatus.COMPLETED,
        progress: 100,
        message: '生成完成'
      });

      // 获取更新后的任务
      const updatedTask = this.activeTasks.get(task.clientId);
      if (!updatedTask) return;

      // 追踪生成完成事件
      this.eventTracker.trackGenerationComplete(updatedTask, {
        success: true,
        totalWaitTime: updatedTask.totalWaitTime,
        actualGenerationTime: (Date.now() - updatedTask.createdAt) / 1000
      });

      callbacks?.onComplete?.(updatedTask);
      this.cleanupTask(task.clientId);
    },

    /** 处理错误阶段 */
    handleErrorStage(task: QueueTask, callbacks?: QueueCallbacks): void {
      this.updateTask(task.clientId, {
        status: GenerationStatus.ERROR_OR_CANCELLED
      });

      // 获取更新后的任务
      const updatedTask = this.activeTasks.get(task.clientId);
      if (!updatedTask) return;
      
      // 追踪生成完成事件
      this.eventTracker.trackGenerationComplete(updatedTask, {
        success: false,
        totalWaitTime: updatedTask.totalWaitTime,
        actualGenerationTime: (Date.now() - updatedTask.createdAt) / 1000,
        errorMessage: updatedTask.message
      });

      callbacks?.onError?.(updatedTask, new Error(updatedTask.message));
      this.cleanupTask(task.clientId);
    }
  }
}); 